'use client'

/**
 * HOME HALF_BANNER_HALF_PRODUCT_TILES PL widget — widget-of-widget container.
 *
 * v2 nested pattern (see PERSONALISED_PRODUCTS / HONEST_REVIEWS): the page renderer pre-renders
 * the nested SINGLE_PRODUCT_CARD children and hands them in via `renderedChildren`; this container
 * arranges them, branching on `shouldUseDesignSystem()` like the non-PL router. The DS branch
 * reuses `half-banner-half-product-tiles-widget-ds`'s exports (shell classes, `HalfBannerBannerDs`,
 * grid/carousel); the legacy branch mirrors `HalfBannerHalfProductTilesLegacy` (same scss module,
 * `HeroBannerV2`, desktop map / mobile Swiper). Both share the 6-tile cap, the
 * `__product_category_carousel` tracker, visibility telemetry, and the non-PL chromes' exact
 * per-card click/wishlist configs (via `CardTrackingProvider`); mini-tile chrome reaches the leaf
 * via `CardDisplayOptionsProvider`. Per-card impressions ride the page-level `productViewTracker`
 * (the non-PL chromes publish no section tracker — same call as HONEST_REVIEWS).
 */

import { WidgetType } from '@lastbrand/storefront-proto-registry/api/v1/enums'
import classnames from 'classnames'
import { useCallback, useMemo } from 'react'

import { PLBaseErrorBoundary } from '@src/components/pl-base-error-boundary'
import { PLTypedWidgetInterface, PLTypedWidgetProps } from '@src/components/pl-base-widget'
import { useWidgetSectionPosition } from '@src/components/pl-base-widget/contexts/widget-page-order'
import { usePLPageMetaOptional } from '@src/components/pl-page-context'
import { getInitialSelectedVariant, resolveAppExclusive } from '@src/components/product-card/product-card.helpers'
import type { IProductCard } from '@src/components/product-card/product-card.types'
import { ProductPrice } from '@src/components/product-card/product-title-section'
import {
  bannerSlotClass,
  containerClass,
  HalfBannerBannerDs,
  innerContainerClass,
  MAX_PRODUCTS_TO_SHOW,
  productsSlotClass,
  ProductTileCarousel,
  ProductTileGrid,
  TRACKER_SUFFIX,
} from '@src/components/widgets/half-banner-half-product-tiles-ds/half-banner-half-product-tiles-widget-ds'
import { PLSectionTrackingWrapper } from '@src/components/widgets/pl-section-tracking-wrapper'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import Swiper from '@src/quince-components/src/atoms/swiper'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useDynamicStyle from '@src/quince-site-common/src/hooks/useDynamicStyle'
import { useTrackIsElementVisible } from '@src/quince-site-common/src/hooks/useTrackIsElementVisible'
import { getValidPromo } from '@src/quince-site-common/src/utils/festivalOffersUtility'
import productTileStyles from '@src/SectionRenderer/sections/commonStyles/newHomePageProductTile.module.scss'
import styles from '@src/SectionRenderer/sections/halfBannerHalfProductTiles/halfBannerHalfProductTiles.module.scss'
import { HeroBannerV2 } from '@src/SectionRenderer/sections/heroBannerV2'
import {
  getContentContainerTrackingMetadata,
  getTrackingContext,
} from '@src/SectionRenderer/sections/productListSection/productListSection.helpers'
import { getFindingMethodAggregate } from '@src/telemetry/findingMethod'
import { shouldUseDesignSystem } from '@src/utils/should-use-design-system'

import { buildHalfBannerHalfProductTilesConfig } from '../half-banner-half-product-tiles-section-builders'
import { CardDisplayOptionsProvider } from '../single-product-card/card-display-options-context'
import { CardTrackingProvider } from '../single-product-card/card-tracking-context'

import type { CardChromeProps } from '../single-product-card/card-display-options-context'
import type {
  HalfBannerHalfProductTilesWidgetData,
  ProductCardData,
} from '@lastbrand/storefront-proto-registry/api/v1/widget'

/** Legacy parity: banner baseDynamicClass injected for Contentful theming (both non-PL chromes). */
// @i18n-ignore
const BASE_DYNAMIC_HEIGHT = 'height--595'

// Widget.widgets is a recursive proto type TS can't resolve through Omit<Widget,'data'>;
// read nested SINGLE_PRODUCT_CARD data via a narrow cast (same pattern as the other home containers).
interface ProductNestedWidget {
  data?: { productItem?: ProductCardData }
}

function HalfBannerHalfProductTilesWidgetComponent(props: PLTypedWidgetProps<HalfBannerHalfProductTilesWidgetData>) {
  const data = props.data.halfBannerHalfProductTilesData

  const deviceInfo = useDeviceInfo()
  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP

  const nestedWidgets = (props as unknown as { widgets?: ProductNestedWidget[] }).widgets
  const renderedChildren = props.renderedChildren

  // Page-level tracker (e.g. 'home_page'), published by the page renderer's PLPageProvider —
  // mirrors non-PL's unconditional `SectionRenderer.tsx` tracker injection. Optional read so the
  // widget stays renderable outside a PLPageProvider (matches other `sectionTracker` consumers).
  const sectionTracker = usePLPageMetaOptional()?.sectionTracker

  // Derived 1-based page position for event context parity (legacy pre-bakes it; PL wire doesn't).
  const sectionPosition = useWidgetSectionPosition(props.id)

  // Section-level proto → domain conversion only — cards render as pre-rendered children.
  const sectionConfig = useMemo(
    () => (data ? buildHalfBannerHalfProductTilesConfig(data, sectionTracker, sectionPosition) : null),
    [data, sectionTracker, sectionPosition],
  )
  const {
    tracker: baseTracker,
    classNamesMobileOverrides,
    classNames,
    mobileDynamicClassesOverrides,
    baseDynamicClasses,
    id,
    sectionType,
    trackingMetaData = {},
  } = sectionConfig ?? {}

  // Legacy parity: built unconditionally — `"undefined__product_category_carousel"` when
  // `baseTracker` is missing — so existing dashboards keep receiving the same tracker value.
  const tracker = `${baseTracker}${TRACKER_SUFFIX}`

  const { styleObject, className } = useDynamicStyle({
    dynamicValueClassesMobileOverride: mobileDynamicClassesOverrides,
    baseDynamicValueClasses: baseDynamicClasses,
    mobileClassNamesOverride: classNamesMobileOverrides,
    baseClassNames: classNames,
    postFix: styles.dynamicClassesPostFix ?? '',
  })

  const getIsVisibleTrackParams = useCallback(() => ({ ...trackingMetaData }), [trackingMetaData])
  const { ref: trackIsVisibleRef } = useTrackIsElementVisible(getIsVisibleTrackParams)

  const contentContainerTrackingMetadata = useMemo(
    () => getContentContainerTrackingMetadata(trackingMetaData),
    [trackingMetaData],
  )

  // Pair product data 1:1 with renderedChildren by original index, drop entries without
  // productItem, THEN cap to 6 — so `productPosition` matches the non-PL chromes' filtered,
  // capped `products.map(...)` index.
  const cardEntries = useMemo(
    () =>
      (Array.isArray(nestedWidgets) ? nestedWidgets : [])
        .flatMap((widget, index) => {
          const product = widget.data?.productItem
          const node = renderedChildren?.[index]?.node
          return product && node ? [{ product, node }] : []
        })
        .slice(0, MAX_PRODUCTS_TO_SHOW),
    [nestedWidgets, renderedChildren],
  )

  // Each node wrapped in its own CardTrackingProvider carrying the EXACT per-card click/wishlist
  // configs the non-PL chromes build — `ProductCard` only emits PRODUCT_CLICK / link attribution
  // when these are present. No section `productViewTracker` (see file header). Memoized so
  // unrelated context updates don't rebuild every provider value; `findingMethod` is therefore
  // snapshotted per memo run — same value at click time, since navigation re-renders the page.
  const tiles = useMemo(
    () =>
      cardEntries.map((entry, idx) => {
        const productCard = { product: entry.product } as unknown as IProductCard
        const trackingContext = {
          ...getTrackingContext(tracker, productCard.product.slug),
          tracker,
        }
        const validPromo = getValidPromo(productCard.product.cardVariants[0]?.promoConfig)

        const productClickTrackConfig = {
          trackInternal: true,
          trackGA: true,
          ...trackingContext,
          context: {
            sectionType,
            sectionid: id,
            ...trackingMetaData.context,
            productPosition: idx,
            ...contentContainerTrackingMetadata,
            findingMethod: getFindingMethodAggregate(),
            is_final_sale: validPromo?.isFinalSale ? 1 : 0,
            is_app_exclusive: resolveAppExclusive(productCard.product.cardVariants[0]) ? 1 : 0,
            productProperties: {
              discounted_price: validPromo?.promoPrice ?? productCard.product.cardVariants[0]?.price,
              price: productCard.product.cardVariants[0]?.price,
            },
          },
        }

        const wishlistTrackConfig = {
          ...trackingContext,
          sectionType,
          sectionid: id,
          ...trackingMetaData.context,
          productPosition: idx,
          ...contentContainerTrackingMetadata,
          findingMethod: getFindingMethodAggregate(),
        }

        return {
          key: String(entry.product.productId),
          idx,
          node: (
            <CardTrackingProvider
              key={String(entry.product.productId)}
              tracker={tracker}
              productClickTrackConfig={productClickTrackConfig}
              wishlistTrackConfig={wishlistTrackConfig}
            >
              {entry.node}
            </CardTrackingProvider>
          ),
        }
      }),
    [cardEntries, tracker, sectionType, id, trackingMetaData, contentContainerTrackingMetadata],
  )

  // Mini-tile chrome shared by both branches — same ProductCard props as the non-PL chromes.
  // The per-branch bags are memoized too: the provider's context value is only as stable as
  // the `cardProps` reference it receives, and an inline spread at the JSX call site would
  // rebuild it (and re-render every card leaf) on each container render.
  const sharedCardProps = useMemo<CardChromeProps>(
    () => ({
      hideWishlistIcon: !isDesktop,
      hideBuyNowIcon: !isDesktop,
      hideTitle: true,
      hideTags: true,
    }),
    [isDesktop],
  )

  const dsCardProps = useMemo<CardChromeProps>(
    () => ({ ...sharedCardProps, hideSubscriptionPrice: true, priceCompareAtFirst: true }),
    [sharedCardProps],
  )

  const legacyCardProps = useMemo<CardChromeProps>(
    () => ({
      ...sharedCardProps,
      wishlistIconClassName: productTileStyles.wishlistWrapper,
      wishlistIconClass: productTileStyles.wishlistIcon,
      allowCustomFooterHover: true,
      showMScroll: false,
      /*
       * The price contract of `renderCardFooter`, published so a card that cannot call it still
       * matches. All three are inert for `ProductCard` itself — `hideTitle: true` means it never
       * renders `ProductTitleSection`, and its footer is replaced wholesale by `customFooter` — but
       * the SVD card takes legacy types nowhere, so it renders its own default footer and needs the
       * same three facts stated as data:
       *
       *  - `productPriceClass` — the footer styles its price with `.productPriceWrapper`
       *    (1.17rem/500). Without it the SVD price inherited the card's own 1.6rem and rendered
       *    visibly larger than the legacy tile beside it.
       *  - `hideSubscriptionPrice` — `renderCardFooter` builds `ProductPrice` without
       *    `subscriptionEnabled`, so the legacy footer never shows a subscription price. Omitting
       *    this left it ON for SVD, which showed a *different, lower* price than legacy
       *    (e.g. $29.90 → $6.17) — a wrong price, not a styling gap.
       * `priceCompareAtFirst` is deliberately NOT set here, though `dsCardProps` sets it. On the DS
       * tile it means "stack compare-at above sale"; the SCSS body maps it to
       * `.priceRowCompareAtFirst` (`flex-direction: column`), whereas legacy's detached footer is a
       * **row** with a 0.4rem gap on desktop and only stacks on mobile. Setting it here measured
       * `column/flex-end` against legacy's `row`. The SCSS body already emits compare-at first, so the
       * order needs no flag; the gap is handled in `svd-product-card.module.scss` under
       * `.titleRowHideName .priceRow`, which is the same "title hidden" condition legacy keys on.
       */
      productPriceClass: styles.productPriceWrapper,
      hideSubscriptionPrice: true,
    }),
    [sharedCardProps],
  )

  // Legacy branch's `ProductPrice`-only footer, built by the leaf (only it holds the product
  // data). Stable reference so it doesn't churn the memoized provider value.
  const renderCardFooter = useCallback((productCard: IProductCard) => {
    const selectedVariant = getInitialSelectedVariant({ cardVariants: productCard.product.cardVariants })
    return (
      <ProductPrice
        productPriceClass={styles.productPriceWrapper}
        productPrefixPriceClassName={styles.productPrefixPrice}
        slug={productCard.product.slug}
        title={productCard.product.title}
        spid={productCard.product.relatedIds.spid}
        reviewSummary={productCard.product.reviews}
        selectedCardVariant={selectedVariant}
        product={productCard.product}
        isNotAttachedToProductTitleSection
      />
    )
  }, [])

  if (!data || !sectionConfig) return null
  if (!tiles.length) return null

  // DS branch — mirrors HalfBannerHalfProductTilesWidgetDs via its exports; the per-tile wrapper
  // carries the positional test locator the DS chrome put on the card itself.
  if (shouldUseDesignSystem()) {
    const dsTiles = tiles.map(tile => (
      <div key={tile.key} data-testid={TEST_LOCATORS.HALF_BANNER_HALF_PRODUCT_TILES.PRODUCT(tile.idx)}>
        {tile.node}
      </div>
    ))

    return (
      <PLSectionTrackingWrapper sectionConfig={sectionConfig} tracker={sectionTracker}>
        <div
          ref={trackIsVisibleRef}
          className={[containerClass, className].filter(Boolean).join(' ')}
          style={styleObject}
          data-testid={TEST_LOCATORS.HALF_BANNER_HALF_PRODUCT_TILES.ROOT}
        >
          <div className={innerContainerClass}>
            <div className={bannerSlotClass}>
              <HalfBannerBannerDs sectionConfig={sectionConfig} tracker={tracker} />
            </div>
            <div className={productsSlotClass}>
              <CardDisplayOptionsProvider
                // eslint-disable-next-line i18next/no-literal-string -- DS tile variant token, not user-facing copy
                dsVariant="mini"
                cardProps={dsCardProps}
              >
                {isDesktop ? <ProductTileGrid tiles={dsTiles} /> : <ProductTileCarousel tiles={dsTiles} />}
              </CardDisplayOptionsProvider>
            </div>
          </div>
        </div>
      </PLSectionTrackingWrapper>
    )
  }

  // Legacy branch — mirrors HalfBannerHalfProductTilesLegacy: HeroBannerV2 banner half, per-tile
  // `.productCard` wrapper, desktop map / mobile Swiper, `ProductPrice`-only footer built by the
  // leaf via `renderCardFooter` (only the leaf holds the product data).
  const legacyTiles = tiles.map(tile => (
    <div className={styles.productCard} key={tile.key}>
      {tile.node}
    </div>
  ))

  return (
    <PLSectionTrackingWrapper sectionConfig={sectionConfig} tracker={sectionTracker}>
      <div className={classnames(styles.container, className)} style={styleObject} ref={trackIsVisibleRef}>
        <div className={classnames(styles.child, styles.bannerContainer)}>
          <HeroBannerV2
            sectionConfig={{ ...sectionConfig, tracker, baseDynamicClasses: [BASE_DYNAMIC_HEIGHT] }}
            contentClassName={styles.contentContainer}
          />
        </div>
        <div className={classnames(styles.child, styles.productsList)}>
          <CardDisplayOptionsProvider cardProps={legacyCardProps} renderCardFooter={renderCardFooter}>
            {isDesktop ? (
              legacyTiles
            ) : (
              <Swiper
                className={styles.swiperContainer}
                numberOfVisibleSlides={3}
                showNavigator={false}
                variableWidth
                spaceBetween={8}
                tracker={tracker}
                // @i18n-ignore
                id={'productListSection'}
                trackingMetaData={trackingMetaData}
              >
                {legacyTiles}
              </Swiper>
            )}
          </CardDisplayOptionsProvider>
        </div>
      </div>
    </PLSectionTrackingWrapper>
  )
}

export const HalfBannerHalfProductTilesWidget: PLTypedWidgetInterface<
  HalfBannerHalfProductTilesWidgetData,
  WidgetType.HALF_BANNER_HALF_PRODUCT_TILES
> = {
  id: WidgetType.HALF_BANNER_HALF_PRODUCT_TILES,
  errorBoundary: PLBaseErrorBoundary,
  render: HalfBannerHalfProductTilesWidgetComponent,
}
